import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    LayoutDashboard,
    CreditCard,
    Layers,
    FileBarChart,
    Building2,
    UserPlus,
    Shield,
    Users,
    LifeBuoy,
    UserCircle,
    Bell,
    Wallet,
    ScrollText,
    Settings,
    X,
    ChevronLeft,
    ChevronRight,
    type LucideIcon,
} from "lucide-react";
import { clsx } from "clsx";
import { useAuth } from "@hooks/useAuth";
import { useAuthStore } from "@stores/auth/authStore";
import {
    ASSOCIATE_DASHBOARD_PERMISSION,
    canAccessPlatformPermission,
    hasExactPermission,
} from "@lib/permissions";

type NavItem = {
    path: string;
    /** Key under `nav.` in the translation files */
    labelKey: string;
    icon: LucideIcon;
    /** If set, user must have this permission (or wildcard match) */
    perm?: string;
    /** Show if any of these permissions match */
    anyPerm?: string[];
    /** Match `perm` literally (no wildcard grants) */
    exact?: boolean;
};

const overviewNav: NavItem[] = [
    { path: "/", labelKey: "overview", icon: LayoutDashboard, perm: "platform.analytics.read" },
    {
        path: "/associate/dashboard",
        labelKey: "myDashboard",
        icon: Wallet,
        perm: ASSOCIATE_DASHBOARD_PERMISSION,
        exact: true,
    },
];

const navGroups: { groupKey: string; items: NavItem[] }[] = [
    {
        groupKey: "platform",
        items: [
            { path: "/payments", labelKey: "payments", icon: CreditCard, perm: "platform.payments.read" },
            { path: "/plans", labelKey: "plans", icon: Layers, perm: "platform.plans.read" },
            {
                path: "/reports/monthly",
                labelKey: "monthlyReports",
                icon: FileBarChart,
                perm: "platform.reports.read",
            },
            {
                path: "/request-logs",
                labelKey: "requestLogs",
                icon: ScrollText,
                perm: "platform.analytics.read",
            },
        ],
    },
    {
        groupKey: "organizations",
        items: [
            { path: "/tenants", labelKey: "tenants", icon: Building2, perm: "platform.tenants.read" },
            {
                path: "/associates",
                labelKey: "associates",
                icon: UserPlus,
                perm: "platform.associates.read",
            },
        ],
    },
    {
        groupKey: "access",
        items: [
            { path: "/owners", labelKey: "owners", icon: Shield, perm: "platform.owners.create" },
            {
                path: "/users",
                labelKey: "userManagement",
                icon: Users,
                anyPerm: ["platform.staff.read", "platform.roles.read"],
            },
        ],
    },
    {
        groupKey: "support",
        items: [
            { path: "/support/tickets", labelKey: "support", icon: LifeBuoy, perm: "support.tickets.read" },
        ],
    },
];

const accountNav: NavItem[] = [
    { path: "/profile", labelKey: "profile", icon: UserCircle },
    { path: "/notifications", labelKey: "notifications", icon: Bell },
    { path: "/settings", labelKey: "settings", icon: Settings },
];

function itemVisible(permissions: string[], item: NavItem): boolean {
    if (item.anyPerm?.length) {
        return item.anyPerm.some((p) => canAccessPlatformPermission(permissions, p));
    }
    if (item.perm) {
        return item.exact
            ? hasExactPermission(permissions, item.perm)
            : canAccessPlatformPermission(permissions, item.perm);
    }
    return true;
}

function isItemActive(pathname: string, path: string): boolean {
    if (path === "/") return pathname === "/";
    return pathname === path || pathname.startsWith(path + "/");
}

interface OwnerSidebarProps {
    isMobileOpen?: boolean;
    onMobileClose?: () => void;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
}

export const OwnerSidebar: React.FC<OwnerSidebarProps> = ({
    isMobileOpen = false,
    onMobileClose,
    isCollapsed = false,
    onToggleCollapse,
}) => {
    const { t } = useTranslation();
    const location = useLocation();
    const { user } = useAuth();
    const permissions = useAuthStore((s) => s.permissions);

    const renderNavItem = (item: NavItem) => {
        const Icon = item.icon;
        const isActive = isItemActive(location.pathname, item.path);
        const label = t(`nav.${item.labelKey}`);
        return (
            <Link
                key={item.path}
                to={item.path}
                onClick={() => onMobileClose?.()}
                aria-current={isActive ? "page" : undefined}
                className={clsx(
                    "group relative flex items-center gap-3 rounded-xl px-3.5 py-2.5 transition-all duration-200",
                    isCollapsed ? "justify-center" : "",
                    isActive
                        ? "bg-gradient-to-r from-primary/10 via-primary/5 to-transparent text-neutral-dark shadow-sm shadow-primary/5 dark:from-primary/20 dark:via-primary/10 dark:text-white rtl:bg-gradient-to-l"
                        : "text-neutral-dark/70 hover:bg-neutral-light/50 hover:text-neutral-dark dark:text-neutral-light/60 dark:hover:bg-white/5 dark:hover:text-white"
                )}
                title={isCollapsed ? label : undefined}
            >
                <div
                    className={clsx(
                        "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-all duration-200",
                        isActive
                            ? "bg-gradient-to-br from-primary to-secondary shadow-lg shadow-primary/20"
                            : "bg-neutral-light/50 group-hover:bg-primary/10 dark:bg-white/5 dark:group-hover:bg-primary/20"
                    )}
                >
                    <Icon
                        size={18}
                        className={clsx("transition-all duration-200", isActive ? "text-white" : "text-current")}
                    />
                </div>
                {!isCollapsed && (
                    <>
                        <span className="whitespace-nowrap text-sm font-medium">{label}</span>
                        {isActive && (
                            <div className="absolute end-3 h-6 w-1.5 rounded-full bg-gradient-to-b from-primary to-secondary" />
                        )}
                    </>
                )}
            </Link>
        );
    };

    const NavContent = () => (
        <nav className="flex-1 space-y-3 overflow-y-auto p-3">
            <div className="space-y-1">
                {overviewNav.filter((i) => itemVisible(permissions, i)).map(renderNavItem)}
            </div>
            {navGroups.map((group) => {
                const visibleItems = group.items.filter((i) => itemVisible(permissions, i));
                if (visibleItems.length === 0) return null;
                return (
                    <div key={group.groupKey} className="space-y-1">
                        {!isCollapsed && (
                            <p className="px-3.5 pt-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-dark/45 dark:text-neutral-light/45">
                                {t(`nav.groups.${group.groupKey}`)}
                            </p>
                        )}
                        {visibleItems.map(renderNavItem)}
                    </div>
                );
            })}
            {!isCollapsed && (
                <div className="mx-3 my-2 border-t border-neutral-border/30 dark:border-neutral-border/10" />
            )}
            {accountNav.filter((i) => itemVisible(permissions, i)).map(renderNavItem)}
        </nav>
    );

    const CollapseIcon = isCollapsed ? ChevronRight : ChevronLeft;

    return (
        <>
            <aside
                className={clsx(
                    "fixed bottom-0 start-0 top-16 z-40 hidden border-e border-neutral-border/30 bg-white/50 backdrop-blur-xl transition-all duration-300 dark:border-neutral-border/10 dark:bg-neutral-dark/50 lg:block",
                    isCollapsed ? "w-24" : "w-72"
                )}
            >
                <div className="flex h-full flex-col">
                    <NavContent />
                    {onToggleCollapse && (
                        <div className="flex-shrink-0 border-t border-neutral-border/30 p-3 dark:border-neutral-border/10">
                            <button
                                type="button"
                                onClick={onToggleCollapse}
                                className={clsx(
                                    "flex w-full items-center gap-3 rounded-xl px-3.5 py-0 text-neutral-dark/70 transition-all duration-200 hover:bg-transparent hover:text-neutral-dark dark:text-neutral-light/70 dark:hover:bg-transparent dark:hover:text-white",
                                    isCollapsed ? "justify-start" : "justify-end"
                                )}
                                title={isCollapsed ? t("nav.sidebar.expand") : t("nav.sidebar.collapse")}
                                aria-label={isCollapsed ? t("nav.sidebar.expand") : t("nav.sidebar.collapse")}
                            >
                                <CollapseIcon size={20} className="rtl:rotate-180" />
                            </button>
                        </div>
                    )}
                </div>
            </aside>
            {isMobileOpen && (
                <>
                    <div
                        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm transition-opacity duration-300 lg:hidden"
                        onClick={onMobileClose}
                        onKeyDown={(e) => e.key === "Escape" && onMobileClose?.()}
                        role="presentation"
                    />
                    <aside className="fixed bottom-0 start-0 top-0 z-50 w-80 border-e border-neutral-border/50 bg-white shadow-2xl dark:border-neutral-border/20 dark:bg-neutral-dark lg:hidden">
                        <div className="flex h-full flex-col">
                            <div className="flex items-center justify-between border-b border-neutral-border/50 p-4 dark:border-neutral-border/20">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary via-secondary to-tertiary shadow-lg shadow-primary/30 ring-2 ring-white/20 dark:ring-neutral-dark/20">
                                        {user?.profilePicture ? (
                                            <img src={user.profilePicture} alt="" className="h-10 w-10 rounded-full" />
                                        ) : (
                                            <span className="text-lg font-bold text-white">M</span>
                                        )}
                                    </div>
                                    <span className="block text-lg font-bold text-neutral-dark dark:text-white">
                                        {t("common.brand")}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={onMobileClose}
                                    aria-label={t("nav.sidebar.closeMenu")}
                                    className="rounded-xl p-2 transition-colors duration-150 hover:bg-neutral-light/50 dark:hover:bg-white/5"
                                >
                                    <X size={20} className="text-neutral-dark dark:text-neutral-light" />
                                </button>
                            </div>
                            <div className="border-b border-neutral-border/30 px-4 py-3 dark:border-neutral-border/10">
                                <h2 className="text-xs font-semibold uppercase tracking-wider text-neutral-dark/40 dark:text-neutral-light/40">
                                    {t("common.console")}
                                </h2>
                            </div>
                            <NavContent />
                        </div>
                    </aside>
                </>
            )}
        </>
    );
};
